import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'

import { cn } from '@/lib/utils/cn'
import { Horizon, Priority, Status, UserRole } from '@/types/enums'
import { Label } from '@/components/ui/label'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

// SelectHorizon Component
interface SelectHorizonProps {
  value?: Horizon | 'all'
  onValueChange: (value: Horizon | 'all') => void
  placeholder?: string
  className?: string
  includeAllOption?: boolean
  allOptionLabel?: string
}

const SelectHorizon = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Root>,
  SelectHorizonProps
>(
  (
    {
      value,
      onValueChange,
      placeholder = 'Select horizon',
      className,
      includeAllOption = false,
      allOptionLabel = 'All Horizons',
      ...props
    },
    ref
  ) => (
    <Select value={value} onValueChange={onValueChange} {...props}>
      <SelectTrigger className={className} ref={ref}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {includeAllOption && <SelectItem value="all">{allOptionLabel}</SelectItem>}
        <SelectItem value={Horizon.None}>{Horizon.None}</SelectItem>
        <SelectItem value={Horizon.Now}>{Horizon.Now}</SelectItem>
        <SelectItem value={Horizon.Next}>{Horizon.Next}</SelectItem>
        <SelectItem value={Horizon.Someday}>{Horizon.Someday}</SelectItem>
        <SelectItem value={Horizon.Never}>{Horizon.Never}</SelectItem>
      </SelectContent>
    </Select>
  )
)
SelectHorizon.displayName = 'SelectHorizon'

// SelectPriority Component
interface SelectPriorityProps {
  value?: Priority | 'all'
  onValueChange: (value: Priority | 'all') => void
  placeholder?: string
  className?: string
  includeAllOption?: boolean
  allOptionLabel?: string
}

const SelectPriority = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Root>,
  SelectPriorityProps
>(
  (
    {
      value,
      onValueChange,
      placeholder = 'Select priority',
      className,
      includeAllOption = false,
      allOptionLabel = 'All Priorities',
      ...props
    },
    ref
  ) => (
    <Select value={value} onValueChange={onValueChange} {...props}>
      <SelectTrigger className={className} ref={ref}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {includeAllOption && <SelectItem value="all">{allOptionLabel}</SelectItem>}
        <SelectItem value={Priority.None}>{Priority.None}</SelectItem>
        <SelectItem value={Priority.High}>{Priority.High}</SelectItem>
        <SelectItem value={Priority.Medium}>{Priority.Medium}</SelectItem>
        <SelectItem value={Priority.Low}>{Priority.Low}</SelectItem>
      </SelectContent>
    </Select>
  )
)
SelectPriority.displayName = 'SelectPriority'

// SelectStatus Component
interface SelectStatusProps {
  value?: Status | 'all'
  onValueChange: (value: Status | 'all') => void
  placeholder?: string
  className?: string
  includeAllOption?: boolean
  allOptionLabel?: string
}

const SelectStatus = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Root>,
  SelectStatusProps
>(
  (
    {
      value,
      onValueChange,
      placeholder = 'Select status',
      className,
      includeAllOption = false,
      allOptionLabel = 'All Statuses',
      ...props
    },
    ref
  ) => (
    <Select value={value} onValueChange={onValueChange} {...props}>
      <SelectTrigger className={className} ref={ref}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {includeAllOption && <SelectItem value="all">{allOptionLabel}</SelectItem>}
        <SelectItem value={Status.Draft}>Draft</SelectItem>
        <SelectItem value={Status.ToDo}>To Do</SelectItem>
        <SelectItem value={Status.InProgress}>In Progress</SelectItem>
        <SelectItem value={Status.Done}>Done</SelectItem>
      </SelectContent>
    </Select>
  )
)
SelectStatus.displayName = 'SelectStatus'

// SelectUserRole Component
interface SelectUserRoleProps {
  value?: string
  onValueChange: (value: UserRole) => void
  label?: string
  placeholder?: string
  error?: string
  disabled?: boolean
  className?: string
  showLabel?: boolean
}

const SelectUserRole = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Root>,
  SelectUserRoleProps
>(
  (
    {
      value,
      onValueChange,
      label = 'Role',
      placeholder = 'Select role',
      error,
      disabled = false,
      className,
      showLabel = true,
      ...props
    },
    ref
  ) => (
    <div className={cn('space-y-2', className)}>
      {showLabel && <Label htmlFor="role">{label}</Label>}
      <Select value={value} onValueChange={onValueChange} disabled={disabled} {...props}>
        <SelectTrigger id="role" ref={ref}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={UserRole.SuperAdmin}>SuperAdmin</SelectItem>
          <SelectItem value={UserRole.Admin}>Admin</SelectItem>
          <SelectItem value={UserRole.Member}>Member</SelectItem>
        </SelectContent>
      </Select>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
)
SelectUserRole.displayName = 'SelectUserRole'

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SelectHorizon,
  SelectPriority,
  SelectStatus,
  SelectUserRole,
}
